// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * Overview tab — at-a-glance stat cards, top-concerns list, and a draft
 * email preview. Click-through callbacks switch the layout to the matching
 * tab.
 */
import { type FC, type ReactNode, useMemo } from 'react'
import { AlertTriangle, CheckCircle2, Copy, FileWarning, Mail, XCircle } from 'lucide-react'
import { Badge, Button } from '@/components/ui'
import { MarkdownViewer } from '@/components/ui/markdown-viewer'

import type {
  ContractDuplicateFinding,
  TroublesomeClauseFinding,
} from '../../hooks/extractors'
import { PageLink, type NegotiationFinding } from './shared'
import { DUPLICATE_CATEGORY_LABEL } from './DuplicatesTab'

interface OverviewTabProps {
  rejectCount: number
  fallbackCount: number
  clauseCount: number
  duplicateCount: number
  highSeverityDuplicateCount: number
  findings: NegotiationFinding[]
  troublesomeClauses: TroublesomeClauseFinding[]
  contractDuplicates: ContractDuplicateFinding[]
  negotiationEmail: string | null
  onOpenEmail: () => void
  onOpenFindings: () => void
  onOpenClauses: () => void
  onOpenDuplicates: () => void
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}

type ConcernKind = 'reject' | 'troublesome' | 'duplicate' | 'fallback'

interface Concern {
  kind: ConcernKind
  label: string
  summary: string | null
  sectionRef: string | null
  pageNumber: number | null
  annotationId: string | null
}

export const OverviewTab: FC<OverviewTabProps> = ({
  rejectCount,
  fallbackCount,
  clauseCount,
  duplicateCount,
  highSeverityDuplicateCount,
  findings,
  troublesomeClauses,
  contractDuplicates,
  negotiationEmail,
  onOpenEmail,
  onOpenFindings,
  onOpenClauses,
  onOpenDuplicates,
  onOpenPage,
  onOpenInDocument,
}) => {
  const topConcerns = useMemo(
    () => buildTopConcerns(findings, troublesomeClauses, contractDuplicates),
    [findings, troublesomeClauses, contractDuplicates],
  )

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
        <StatCard
          label="Rejects"
          value={rejectCount}
          tone="red"
          icon={<XCircle className="h-5 w-5 text-red-500" />}
          description="Tracked changes UMD cannot accept"
          onClick={rejectCount > 0 ? onOpenFindings : undefined}
        />
        <StatCard
          label="Fallbacks"
          value={fallbackCount}
          tone="amber"
          icon={<AlertTriangle className="h-5 w-5 text-amber-500" />}
          description="Changes needing improvement"
          onClick={fallbackCount > 0 ? onOpenFindings : undefined}
        />
        <StatCard
          label="Troublesome Clauses"
          value={clauseCount}
          tone="red"
          icon={<FileWarning className="h-5 w-5 text-red-500" />}
          description="Clauses flagged for legal review"
          onClick={clauseCount > 0 ? onOpenClauses : undefined}
        />
        <StatCard
          label="Duplicates"
          value={duplicateCount}
          tone="indigo"
          icon={<Copy className="h-5 w-5 text-indigo-500" />}
          description={
            highSeverityDuplicateCount > 0
              ? `${highSeverityDuplicateCount} high-severity`
              : 'Redundant or conflicting provisions'
          }
          onClick={duplicateCount > 0 ? onOpenDuplicates : undefined}
        />
        <StatCard
          label="Negotiation Email"
          value={negotiationEmail ? 1 : 0}
          tone="green"
          icon={<Mail className="h-5 w-5 text-green-600" />}
          description={negotiationEmail ? 'Draft email ready to review' : 'No draft yet'}
          onClick={negotiationEmail ? onOpenEmail : undefined}
          hideValue
        />
      </div>

      {topConcerns.length > 0 && (
        <section aria-labelledby="top-concerns-heading">
          <h2 id="top-concerns-heading" className="text-sm font-semibold px-1 py-1 mb-2">Top Concerns</h2>
          <div className="divide-y rounded border">
            {topConcerns.map((c, i) => (
              <TopConcernRow
                key={i}
                concern={c}
                onOpenPage={onOpenPage}
                onOpenInDocument={onOpenInDocument}
              />
            ))}
          </div>
        </section>
      )}

      {negotiationEmail && (
        <section aria-labelledby="email-preview-heading">
          <div className="flex items-center justify-between mb-2">
            <h2 id="email-preview-heading" className="text-sm font-semibold px-1 py-1 flex items-center gap-2">
              <Mail className="h-4 w-4 text-primary" />
              Draft Negotiation Email
            </h2>
            <Button variant="outline" size="sm" onClick={onOpenEmail}>
              View full email →
            </Button>
          </div>
          <div className="px-4 py-3 bg-muted/30 rounded border max-h-72 overflow-hidden relative">
            <MarkdownViewer content={negotiationEmail} />
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none" />
          </div>
        </section>
      )}

      {rejectCount === 0 && fallbackCount === 0 && clauseCount === 0 && duplicateCount === 0 && (
        <div className="rounded border bg-green-50 px-4 py-6 text-center">
          <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" aria-hidden="true" />
          <p className="text-sm font-medium text-green-900">No concerns identified</p>
          <p className="text-xs text-green-700 mt-1">
            The contract meets UMD's preferred criteria across all reviewed knowledge base items.
          </p>
        </div>
      )}
    </div>
  )
}

function buildTopConcerns(
  findings: NegotiationFinding[],
  troublesomeClauses: TroublesomeClauseFinding[],
  contractDuplicates: ContractDuplicateFinding[],
): Concern[] {
  const items: Concern[] = []
  findings.forEach((f, i) => {
    if (f.status === 'reject') {
      items.push({
        kind: 'reject',
        label: f.summary ?? 'Rejected change',
        summary: f.recommendation || null,
        sectionRef: f.sectionReference,
        pageNumber: f.pageNumber,
        annotationId: `neg-${i}`,
      })
    }
  })
  troublesomeClauses.forEach((c, i) => {
    items.push({
      kind: 'troublesome',
      label: c.issue || 'Troublesome clause',
      summary: c.summary || null,
      sectionRef: c.sectionReference,
      pageNumber: c.pageNumber,
      annotationId: `clause-${i}`,
    })
  })
  contractDuplicates.forEach((d, i) => {
    if (d.severity !== 'HIGH') return
    items.push({
      kind: 'duplicate',
      label: DUPLICATE_CATEGORY_LABEL[d.category],
      summary: d.explanation || null,
      sectionRef: d.locations[0] ?? null,
      pageNumber: null,
      annotationId: d.excerpts.length > 0 ? `dup-${i}-0` : null,
    })
  })
  findings.forEach((f, i) => {
    if (f.status === 'fallback') {
      items.push({
        kind: 'fallback',
        label: f.summary ?? 'Needs improvement',
        summary: f.recommendation || null,
        sectionRef: f.sectionReference,
        pageNumber: f.pageNumber,
        annotationId: `neg-${i}`,
      })
    }
  })
  return items.slice(0, 10)
}

const TopConcernRow: FC<{
  concern: Concern
  onOpenPage?: (page: number) => void
  onOpenInDocument?: (annotationId: string) => void
}> = ({ concern: c, onOpenPage, onOpenInDocument }) => (
  <div className="flex items-start gap-3 px-3 py-2">
    {c.kind === 'troublesome'
      ? <FileWarning className="h-4 w-4 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
      : c.kind === 'reject'
      ? <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" aria-hidden="true" />
      : c.kind === 'duplicate'
      ? <Copy className="h-4 w-4 text-indigo-500 shrink-0 mt-0.5" aria-hidden="true" />
      : <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" aria-hidden="true" />}
    <div className="flex-1 min-w-0">
      <span className="text-sm">{c.label}</span>
      {c.summary && (
        <p className="text-xs text-muted-foreground mt-0.5">{c.summary}</p>
      )}
    </div>
    {c.sectionRef && (
      <Badge variant="secondary" className="font-mono text-xs px-2 py-0 mt-0.5">{c.sectionRef}</Badge>
    )}
    {c.pageNumber != null && <span className="mt-0.5"><PageLink page={c.pageNumber} onOpen={onOpenPage} /></span>}
    {onOpenInDocument && c.annotationId && (
      <button
        type="button"
        onClick={() => onOpenInDocument(c.annotationId!)}
        className="text-xs text-primary underline decoration-primary/40 hover:decoration-primary mt-0.5 cursor-alias"
        title="View in document"
      >
        View
      </button>
    )}
    <Badge variant="outline" className={`text-xs px-2 py-0 mt-0.5 ${
      c.kind === 'fallback'
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : c.kind === 'duplicate'
        ? 'bg-indigo-100 text-indigo-800 border-indigo-200'
        : 'bg-red-100 text-red-800 border-red-200'
    }`}>
      {c.kind === 'troublesome'
        ? 'Clause'
        : c.kind === 'reject'
        ? 'Reject'
        : c.kind === 'duplicate'
        ? 'Duplicate'
        : 'Fallback'}
    </Badge>
  </div>
)

interface StatCardProps {
  label: string
  value: number
  tone: 'red' | 'amber' | 'green' | 'indigo'
  icon: ReactNode
  description: string
  onClick?: () => void
  hideValue?: boolean
}

const StatCard: FC<StatCardProps> = ({ label, value, tone, icon, description, onClick, hideValue }) => {
  const toneClass = tone === 'red'
    ? 'border-red-200 bg-red-50'
    : tone === 'amber'
    ? 'border-amber-200 bg-amber-50'
    : tone === 'indigo'
    ? 'border-indigo-200 bg-indigo-50'
    : 'border-green-200 bg-green-50'
  const valueColor = value === 0
    ? 'text-muted-foreground'
    : tone === 'red'
    ? 'text-red-700'
    : tone === 'amber'
    ? 'text-amber-700'
    : tone === 'indigo'
    ? 'text-indigo-700'
    : 'text-green-700'
  const interactive = onClick && value > 0
  return (
    <div
      className={`rounded border px-4 py-3 ${value > 0 ? toneClass : 'bg-muted/30'} ${interactive ? 'cursor-pointer hover:shadow-sm transition-shadow' : ''}`}
      onClick={interactive ? onClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick() } : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</span>
        {icon}
      </div>
      {!hideValue && <div className={`text-3xl font-semibold mt-1 ${valueColor}`}>{value}</div>}
      <p className={`text-xs text-muted-foreground ${hideValue ? 'mt-3' : 'mt-1'}`}>{description}</p>
    </div>
  )
}
